/**
 * Creative Error Messages
 * 
 * Replaces boring technical error messages with fun, 
 * anime-themed alternatives. Correctly attributes network/rate-limit
 * issues to AniList's API rather than our own servers.
 */

const CREATIVE_NETWORK_MESSAGES = [
    "📡 AniList's servers drifted into the Spirit World... They'll be back shortly!",
    "🌀 AniList used Rate Limit! It's super effective... Give it a moment!",
    "⚡ AniList's API took a power nap. It does that sometimes — try again in a bit!",
    "🛸 AniList's data got abducted by aliens. They usually return it quickly!",
    "🌊 AniList got swept away by a tsunami of requests. Wave's passing — retry soon!",
    "🐉 A dragon attacked AniList's servers! They're rebuilding as we speak...",
    "🍜 AniList went out for ramen. They'll be back before the noodles get cold!",
    "🌙 AniList entered a filler arc... Skip ahead and retry in a few seconds!",
    "💤 AniList dozed off during a long monologue. Poke it with a retry!",
    "🎭 Plot twist! AniList vanished. Try again for the next episode!",
    "🗡️ AniList's connection was defeated in battle. Revive it with a retry!",
    "🌪️ A portal swallowed AniList's response. They'll send another!",
    "🎮 AniList disconnected! Press retry to continue from your last save point.",
    "🔮 The crystal ball says... AniList is busy. The prophecy suggests retrying!",
    "🚀 Houston, AniList has a problem. Initiating retry sequence...",
];

const CREATIVE_RATE_LIMIT_MESSAGES = [
    "⏳ AniList said 'too fast, senpai!' — Their rate limit kicked in. Wait a moment!",
    "🐌 AniList needs a breather — too many requests at once. Slow down and retry!",
    "🚦 AniList's traffic light turned red. Wait for green and try again!",
    "⏸️ AniList hit the pause button — too many requests. Give it 30 seconds!",
    "🧘 AniList is meditating... It asked for some space. Try again shortly!",
    "🎵 AniList put you on hold — please enjoy the elevator music and retry!",
    "📬 AniList's mailbox is full! Wait a bit before sending more requests.",
    "🏃 You're running too fast for AniList! Let it catch its breath.",
];

const CREATIVE_USER_NOT_FOUND_MESSAGES = [
    "👻 That username vanished into thin air! Double-check the spelling — even ninja can't find them.",
    "🔍 No trace of that user on AniList! Either they're hiding or the username has a typo.",
    "🌀 That username doesn't exist in this dimension. Check for typos!",
    "🕵️ Our best detectives searched AniList — no one by that name. Verify the spelling!",
    "📭 AniList returned an empty envelope — no such user found. Got the right username?",
    "🗺️ Searched every corner of AniList — that username is off the map! Try again carefully.",
    "🐱‍👤 That user is either a ghost or a typo. Double-check and retry!",
];

// Keywords that indicate a network-related error
const NETWORK_ERROR_PATTERNS = [
    'network error',
    'failed to fetch',
    'timeout',
    'timed out',
    'cors',
    'err_connection',
    'err_internet',
    'net::err',
    '502',
    '503',
    '504',
    'bad gateway',
    'service unavailable',
    'gateway timeout',
    'fetch error',
    'connection refused',
    'connection reset',
    'dns',
    'econnrefused',
    'enotfound',
    'socket hang up',
];

// Keywords that indicate an AniList rate limit
const RATE_LIMIT_PATTERNS = [
    'rate limit',
    'too many requests',
    '429',
    'rate-limited',
    'throttled',
];

// Keywords that indicate a user-not-found error
const USER_NOT_FOUND_PATTERNS = [
    'not found',
    '404',
    'user not found',
    'no user',
    'invalid user',
];

/**
 * Checks if an error message matches any pattern in a given list
 */
function matchesPattern(message, patterns) {
    if (!message || typeof message !== 'string') return false;
    const lower = message.toLowerCase();
    return patterns.some(pattern => lower.includes(pattern));
}

/**
 * Returns a random message from a pool
 */
function getRandomMessage(pool) {
    return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Transforms an error message into a creative one.
 * - Rate limit errors → AniList rate limit messages
 * - Network errors → AniList server messages  
 * - User not found → creative username error messages
 * - Other errors pass through unchanged.
 * 
 * @param {string} errorMessage - The original error message
 * @returns {string} - Creative message if matched, original otherwise
 */
export function getCreativeError(errorMessage) {
    if (!errorMessage || typeof errorMessage !== 'string') return errorMessage;

    // 1. Check for rate limit errors first (most specific)
    if (matchesPattern(errorMessage, RATE_LIMIT_PATTERNS)) {
        return getRandomMessage(CREATIVE_RATE_LIMIT_MESSAGES);
    }

    // 2. Check for user-not-found errors
    if (matchesPattern(errorMessage, USER_NOT_FOUND_PATTERNS)) {
        return getRandomMessage(CREATIVE_USER_NOT_FOUND_MESSAGES);
    }

    // 3. Check for network errors
    if (matchesPattern(errorMessage, NETWORK_ERROR_PATTERNS)) {
        return getRandomMessage(CREATIVE_NETWORK_MESSAGES);
    }

    // 4. For compound messages like "Failed to update status: Failed to fetch"
    const colonIndex = errorMessage.lastIndexOf(':');
    if (colonIndex > 0) {
        const tail = errorMessage.substring(colonIndex + 1).trim();
        const prefix = errorMessage.substring(0, colonIndex).trim();

        if (matchesPattern(tail, RATE_LIMIT_PATTERNS)) {
            return `${prefix} — ${getRandomMessage(CREATIVE_RATE_LIMIT_MESSAGES)}`;
        }
        if (matchesPattern(tail, NETWORK_ERROR_PATTERNS)) {
            return `${prefix} — ${getRandomMessage(CREATIVE_NETWORK_MESSAGES)}`;
        }
    }

    return errorMessage;
}

/**
 * Returns a creative user-not-found message for comparison pages
 * @param {string} username - The username that wasn't found
 * @returns {string} - Creative not-found message
 */
export function getCreativeUserNotFound(username) {
    const messages = [
        `👻 "${username}" vanished into thin air! Double-check the spelling — even ninja can't find them on AniList.`,
        `🔍 No trace of "${username}" on AniList! Either they're hiding or the username has a typo.`,
        `🌀 "${username}" doesn't exist in this dimension of AniList. Check for typos and try again!`,
        `🕵️ Our best detectives searched all of AniList — no one named "${username}". Verify the spelling!`,
        `📭 AniList returned an empty envelope — no user "${username}" found. Got the right username?`,
        `🗺️ Searched every corner of AniList — "${username}" is off the map! Check for typos.`,
        `🐱‍👤 "${username}" is either a ghost or a typo. Double-check the username and try again!`,
        `🎭 "${username}" pulled a disappearing act! They don't exist on AniList — or did you misspell?`,
    ];
    return messages[Math.floor(Math.random() * messages.length)];
}

export default getCreativeError;

